import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { canProvisionFreeDashTeam } from "@mirage/service-provisioning";
import Sentry from "@mirage/shared/sentry";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import { useCallback, useState } from "react";
import { RedirectVariant } from "../types";

export const isOnWebOnboarding = (pathname: string, protocol: string) => {
  // allow /authconfirm so users can create connections during onboarding
  return (
    pathname.toLowerCase().includes("/authconfirm") ||
    // Always show for desktop
    (pathname.toLowerCase().includes("/setup") &&
      protocol.toLowerCase().startsWith("http"))
  );
};

export const isSelfServeWebOnboarding = (
  pathname: string,
  protocol: string,
) => {
  return (
    (pathname.toLowerCase().includes("/setup/admin-self-serve") ||
      pathname.toLowerCase().includes("/setup/self-serve")) &&
    protocol.toLowerCase().startsWith("http")
  );
};

export const useWebOnboardingEligibility = () => {
  const [canCreateFreeDashTeam, setCanCreateFreeDashTeam] = useState<
    boolean | null
  >(null);

  const meetsDashOnboardingEligibilityCriteria = useCallback(
    async (variant: RedirectVariant): Promise<boolean> => {
      if (
        variant === RedirectVariant.DfbOnboarding ||
        variant === RedirectVariant.BridgeOnboarding
      ) {
        return true;
      }

      if (canCreateFreeDashTeam !== null) {
        return canCreateFreeDashTeam;
      }

      try {
        const result = await canProvisionFreeDashTeam();
        setCanCreateFreeDashTeam(result);
        return result;
      } catch (error) {
        Sentry.captureException(error);
        setCanCreateFreeDashTeam(false);
        return false;
      }
    },
    [canCreateFreeDashTeam],
  );

  const shouldShowRedirectModal = useCallback(
    async ({
      variant,
      pathname,
      protocol,
    }: {
      variant: RedirectVariant;
      pathname: string;
      protocol: string;
    }): Promise<boolean> => {
      const isDesktop = EnvCtx.surface == "desktop";

      const isEligibleForOnboarding =
        await meetsDashOnboardingEligibilityCriteria(variant);

      const isCurrentlyOnOnboardingPage = isOnWebOnboarding(pathname, protocol);
      const isCurrentlyOnSelfServeOnboardingPage = isSelfServeWebOnboarding(
        pathname,
        protocol,
      );

      // Wait for async result or if still loading
      if (isEligibleForOnboarding === null) {
        return false;
      }

      // If the user is eligible for onboarding and is loading an /embed page from within an iframe,
      // for example, /embed/chat from FSS for the chat sidebar, then suppress redirect modal
      // so they're not attempting to onboard from within an iframed sidebar
      if (isEligibleForOnboarding && isEmbedPage(pathname)) {
        return false;
      }

      if (!isDesktop && variant === RedirectVariant.BridgeOnboarding) {
        // Bridge onboarding is a special case where we always want to show the redirect modal
        // regardless of the user's eligibility or current page.
        return false;
      }

      // 1. If the user is ineligible because of a non-onboarding reason (unverified email, no license, HIPAA, etc),
      // then block user from SS onboarding using redirect modal
      // 2. If the user is ineligible because they require onboarding OR there is no redirect variant,
      // then check if they're currently on onboarding page and if they are eligible to create a Freemium team
      // 3. If the user is currently on onboarding page and is eligible for SS onboarding, then suppress redirect modal
      // 4. If the user is not currently on the onboarding page, then show the redirect modal
      // (either because user requires onboarding or there is some other reason they cannot access SS onboarding)
      if (
        (!variant || variant === RedirectVariant.DfbOnboarding) &&
        isEligibleForOnboarding &&
        isCurrentlyOnOnboardingPage
      ) {
        return false; // Suppress redirect modal when user is on onboarding page and is eligible
      }

      // Prevents RedirectModal from showing for first admins of self-serve teams while they are on the onboarding page
      // First admins of self-serve teams are initially marked as blocked_users, which returns GenericBlock variant.
      if (
        variant === RedirectVariant.GenericBlock &&
        isEligibleForOnboarding &&
        isCurrentlyOnSelfServeOnboardingPage
      ) {
        return false;
      }

      return true;
    },
    [meetsDashOnboardingEligibilityCriteria],
  );

  return {
    meetsDashOnboardingEligibilityCriteria,
    shouldShowRedirectModal,
  };
};
